import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Clock, ArrowLeft, CalendarDays, CheckCircle2, XCircle, Hourglass, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OLAY_TIPLERI = [
  { value: 'sure_uzatma', label: 'Süre Uzatma', color: '#f59e0b' },
  { value: 'durus', label: 'İş Durması', color: '#ef4444' },
  { value: 'mobilizasyon', label: 'Mobilizasyon', color: '#3b82f6' },
  { value: 'yer_teslimi', label: 'Yer Teslimi', color: '#10b981' },
  { value: 'is_baslangic', label: 'İş Başlangıcı', color: '#22c55e' },
  { value: 'hakedis', label: 'Hakediş', color: '#8b5cf6' },
  { value: 'gecici_kabul', label: 'Geçici Kabul', color: '#06b6d4' },
  { value: 'kesin_kabul', label: 'Kesin Kabul', color: '#2d7a4f' },
  { value: 'genel', label: 'Genel Not', color: '#6b7280' },
];

const ONAY_DURUMLARI = [
  { value: 'beklemede', label: 'Beklemede', color: '#f59e0b', icon: Hourglass },
  { value: 'onaylandi', label: 'Onaylandı', color: '#22c55e', icon: CheckCircle2 },
  { value: 'reddedildi', label: 'Reddedildi', color: '#ef4444', icon: XCircle },
];

const emptyForm = { olay_tipi: 'genel', baslik: '', aciklama: '', tarih: '', gun_sayisi: '', onay_durumu: 'beklemede' };

export default function ISYSureTakip({ ihaleler, onBack }) {
  const [selectedIhale, setSelectedIhale] = useState(null);
  const [olaylar, setOlaylar] = useState([]);
  const [toplamUzatma, setToplamUzatma] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchSure = useCallback(async (ihaleId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/isy/sure/${ihaleId}`, { withCredentials: true });
      setOlaylar(res.data.olaylar || []);
      setToplamUzatma(res.data.toplam_uzatma_gun || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedIhale) fetchSure(selectedIhale.id); }, [selectedIhale, fetchSure]);

  const openCreate = () => { setFormData({ ...emptyForm }); setEditMode(false); setSelectedId(null); setDialogOpen(true); };

  const openEdit = (olay) => {
    setFormData({
      olay_tipi: olay.olay_tipi || 'genel', baslik: olay.baslik || '',
      aciklama: olay.aciklama || '', tarih: olay.tarih || '',
      gun_sayisi: olay.gun_sayisi?.toString() || '', onay_durumu: olay.onay_durumu || 'beklemede'
    });
    setEditMode(true); setSelectedId(olay.id); setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.baslik.trim() || !formData.tarih) { toast.error('Başlık ve tarih zorunludur'); return; }
    setSaving(true);
    try {
      const payload = { ...formData, gun_sayisi: formData.gun_sayisi ? parseInt(formData.gun_sayisi) : null };
      if (editMode) {
        await axios.put(`${API}/isy/sure/olay/${selectedId}`, payload, { withCredentials: true });
        toast.success('Olay güncellendi');
      } else {
        await axios.post(`${API}/isy/sure/${selectedIhale.id}`, payload, { withCredentials: true });
        toast.success('Olay eklendi');
      }
      setDialogOpen(false); fetchSure(selectedIhale.id);
    } catch (e) { toast.error('Hata oluştu'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/isy/sure/olay/${deleteId}`, { withCredentials: true });
      toast.success('Olay silindi'); setDeleteId(null); fetchSure(selectedIhale.id);
    } catch (e) { toast.error('Silme hatası'); }
  };

  const getOlayTipi = (tip) => OLAY_TIPLERI.find(o => o.value === tip) || OLAY_TIPLERI[OLAY_TIPLERI.length - 1];
  const getOnayDurum = (d) => ONAY_DURUMLARI.find(o => o.value === d) || ONAY_DURUMLARI[0];

  // Gecikme hesaplama
  const hesaplaGecikme = () => {
    if (!selectedIhale?.orijinal_bitis_tarihi) return null;
    const orijinal = new Date(selectedIhale.orijinal_bitis_tarihi);
    const revize = selectedIhale.revize_bitis_tarihi ? new Date(selectedIhale.revize_bitis_tarihi) : null;
    const bitis = revize || orijinal;
    const bugun = new Date();
    if (bugun <= bitis) return null;
    const gecikmeMs = bugun - bitis;
    return Math.floor(gecikmeMs / (1000 * 60 * 60 * 24));
  };

  if (!selectedIhale) {
    return (
      <div className="space-y-4" data-testid="isy-sure-takip">
        <h2 className="text-lg font-bold text-white">Süre & Süreç Takibi</h2>
        <p className="text-sm text-gray-400">Takip etmek istediğiniz ihaleyi seçin:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(ihaleler || []).map(ih => (
            <Card key={ih.id} className="bg-gray-900/60 border-gray-700/50 hover:border-amber-500/40 cursor-pointer transition-colors" onClick={() => setSelectedIhale(ih)} data-testid={`sure-select-${ih.id}`}>
              <CardContent className="py-3 px-4">
                <p className="text-sm font-semibold text-white">{ih.is_adi}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Süre: {ih.sozlesme_suresi_gun || '-'} gün | Bitiş: {ih.revize_bitis_tarihi || ih.orijinal_bitis_tarihi || '-'}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const gecikmeGun = hesaplaGecikme();

  return (
    <div className="space-y-4" data-testid="isy-sure-takip">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedIhale(null)} className="text-gray-400 hover:text-white h-8 px-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
          </Button>
          <div>
            <h2 className="text-sm font-bold text-white">{selectedIhale.is_adi}</h2>
            <p className="text-xs text-gray-500">Süre & Süreç Takibi</p>
          </div>
        </div>
        <Button onClick={openCreate} size="sm" className="bg-[#1e3a5f] hover:bg-[#2a4f7f] text-white" data-testid="sure-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Olay Ekle
        </Button>
      </div>

      {/* Süre Özet Kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-4 pb-3 text-center">
            <Clock className="w-5 h-5 text-blue-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{selectedIhale.sozlesme_suresi_gun || '-'}</p>
            <p className="text-xs text-gray-500">Sözleşme Süresi (gün)</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-4 pb-3 text-center">
            <CalendarDays className="w-5 h-5 text-amber-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-amber-400">{toplamUzatma}</p>
            <p className="text-xs text-gray-500">Onaylı Süre Uzatma (gün)</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-4 pb-3 text-center">
            <CalendarDays className="w-5 h-5 text-green-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-green-400">{selectedIhale.revize_bitis_tarihi || selectedIhale.orijinal_bitis_tarihi || '-'}</p>
            <p className="text-xs text-gray-500">Bitiş Tarihi</p>
          </CardContent>
        </Card>
        {gecikmeGun !== null && (
          <Card className="bg-red-900/30 border-red-700/50">
            <CardContent className="pt-4 pb-3 text-center">
              <Clock className="w-5 h-5 text-red-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-red-400">{gecikmeGun}</p>
              <p className="text-xs text-red-300">Gecikme (gün)</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="text-center text-gray-400 py-8">Yükleniyor...</div>
      ) : olaylar.length === 0 ? (
        <Card className="bg-gray-900/60 border-gray-700/50">
          <CardContent className="py-12 text-center text-gray-500">
            Henüz olay kaydı yok. <button onClick={openCreate} className="text-blue-400 underline ml-1">İlk olayı ekle</button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {olaylar.map((olay, i) => {
            const tip = getOlayTipi(olay.olay_tipi);
            const onay = getOnayDurum(olay.onay_durumu);
            const OnayIcon = onay.icon;
            return (
              <div key={olay.id} className="flex gap-3 items-start" data-testid={`sure-olay-${olay.id}`}>
                {/* Timeline dot */}
                <div className="flex flex-col items-center mt-1">
                  <div className="w-3 h-3 rounded-full border-2" style={{ borderColor: tip.color, background: i === 0 ? tip.color : 'transparent' }} />
                  {i < olaylar.length - 1 && <div className="w-0.5 flex-1 bg-gray-700 mt-1 min-h-[40px]" />}
                </div>
                {/* Content */}
                <Card className="flex-1 bg-gray-900/60 border-gray-700/50">
                  <CardContent className="py-2.5 px-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge style={{ background: tip.color + '22', color: tip.color, border: `1px solid ${tip.color}44` }} className="text-[10px] px-1.5 py-0">{tip.label}</Badge>
                          <span className="text-xs text-gray-500">{olay.tarih}</span>
                          {olay.gun_sayisi && <span className="text-xs text-amber-400">{olay.gun_sayisi} gün</span>}
                          <OnayIcon className="w-3.5 h-3.5" style={{ color: onay.color }} />
                        </div>
                        <p className="text-sm text-white mt-1">{olay.baslik}</p>
                        {olay.aciklama && <p className="text-xs text-gray-400 mt-0.5">{olay.aciklama}</p>}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-amber-400" onClick={() => openEdit(olay)}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-red-400" onClick={() => setDeleteId(olay.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">{editMode ? 'Olayı Düzenle' : 'Yeni Olay Ekle'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div>
              <Label className="text-gray-400 text-xs">Olay Tipi</Label>
              <Select value={formData.olay_tipi} onValueChange={v => setFormData({...formData, olay_tipi: v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {OLAY_TIPLERI.map(t => <SelectItem key={t.value} value={t.value} className="text-gray-300">{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Tarih *</Label>
              <Input type="date" value={formData.tarih} onChange={e => setFormData({...formData, tarih: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="sure-form-tarih" />
            </div>
            <div className="col-span-2">
              <Label className="text-gray-400 text-xs">Başlık *</Label>
              <Input value={formData.baslik} onChange={e => setFormData({...formData, baslik: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="sure-form-baslik" />
            </div>
            {formData.olay_tipi === 'sure_uzatma' && (
              <>
                <div>
                  <Label className="text-gray-400 text-xs">Gün Sayısı</Label>
                  <Input type="number" value={formData.gun_sayisi} onChange={e => setFormData({...formData, gun_sayisi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="sure-form-gun" />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Onay Durumu</Label>
                  <Select value={formData.onay_durumu} onValueChange={v => setFormData({...formData, onay_durumu: v})}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {ONAY_DURUMLARI.map(d => <SelectItem key={d.value} value={d.value} className="text-gray-300">{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="col-span-2">
              <Label className="text-gray-400 text-xs">Açıklama</Label>
              <Textarea value={formData.aciklama} onChange={e => setFormData({...formData, aciklama: e.target.value})} className="bg-gray-800 border-gray-700 text-white text-sm min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-gray-400">İptal</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2a4f7f] text-white" data-testid="sure-form-save">
              {saving ? 'Kaydediliyor...' : editMode ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-gray-900 border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Olayı Sil</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">Bu olayı silmek istediğinize emin misiniz?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 text-gray-300 border-gray-700">İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
